import moment from "moment";
import { observable, action, runInAction } from "mobx";

import rootService from "../@data/service";

const { getProviderBusinessLocationMarkerMap } = rootService;
import { getTimeString } from "@utils/time";
import { isBrowser } from "@utils/Browser";

class ProfileLocationHoursStore {
	@observable base64Map;

	constructor(rootStore) {
		this.rootStore = rootStore;
	}

	get provider() {
		const { profileStore } = this.rootStore.stores;
		return profileStore.provider;
	}

	get proLocation() {
		return this.provider?.business_location;
	}

	get directionsMapUrl() {
		const lat = this.provider?.business_location?.lat;
		const lng = this.provider?.business_location?.lng;
		const company_name = this.provider?.company_name.replace(/\s/g, "+");
		const address = this.provider?.business_location?.address.replace(
			/\s/g,
			"+"
		);
		const city = this.provider?.business_location?.city.replace(/\s/g, "+");
		const state = this.provider?.business_location?.state
			.replace(/\s/g, "+")
			.toUpperCase();
		const zip = this.provider?.business_location?.zip;
		const address2 = this.provider?.business_location?.address2.replace(
			/\s/g,
			"+"
		);
		return `https://www.google.com/maps?f=d&view=map&q=${company_name}&daddr=${address}+${address2},+${city},+${state}+${zip}&address=${address}+${address2},+${city},+${state}+${zip}`;
	}

	get _hrsDiff() {
		if (!isBrowser) return 0;
		const proDate = new Date(
			this.now.toLocaleString("en-US", {
				timeZone: this.provider?.hours?.timezone,
			})
		);
		const userDate = this.now;
		const timeDiff = userDate.getTime() - proDate.getTime();
		return timeDiff / (1000 * 3600);
	}

	get now() {
		return new Date();
	}

	nowMoment(format) {
		if (!format) {
			throw new Error("No time format provided");
		}
		const now = moment();
		return moment(now, format);
	}

	militaryMoment(time) {
		return moment(time, "HHmm");
	}

	get todayHours() {
		const dayNum = this.now.getDay();
		const day = this.provider?.hours[this.dayStr(dayNum)] || {};
		return {
			...day,
			dayStr: this.dayAbbrev(dayNum),
			dayNum,
			stopStr: this.getTimeString(day.stop),
			startStr: this.getTimeString(day.start),
		};
	}

	get isOpen() {
		if (!this.todayHours?.active) return false;
		const hrs = this.nowMoment("HH").add(this._hrsDiff, "h");
		const mins = this.nowMoment("mm");

		const militaryTime = `${hrs.format("HH")}${mins.format("mm")}`;
		const now = this.militaryMoment(militaryTime);

		const proTodayStart = this.militaryPad(this.todayHours.start);
		const proTodayStop = this.militaryPad(this.todayHours.stop);

		return (
			now.isAfter(this.militaryMoment(proTodayStart)) &&
			now.isBefore(this.militaryMoment(proTodayStop))
		);
	}

	militaryPad(time) {
		if (time === 30) {
			// 0030 - half past midnight
			return `00${time}`;
		}
		if (String(time).length < 2) {
			return `0${time}00`;
		}
		if (String(time).length < 3) {
			return `${time}00`;
		}
		if (String(time).length < 4) {
			return `0${time}`;
		}
		return String(time);
	}

	get isOpenSoon() {
		if (!this.todayHours?.active) return false;
		if (this.isOpen) return false;
		return true;
	}

	get isOpen24Hours() {
		if (!this.todayHours?.active) return false;
		return this.todayHours.start === 0 && this.todayHours.stop === 24;
	}

	get nextOpenDate() {
		if (this.isOpen) return;
		const hasOpenDay = Object.values(this.provider?.hours).find(
			(day) => day.active
		);
		if (!hasOpenDay) return;
		const dayNum = new Date().getDay();
		for (let i = dayNum; i < 7; i++) {
			const day = this.provider?.hours[this.dayStr(i)];
			if (day.active)
				return {
					...day,
					dayStr: this.dayAbbrev(i),
					dayNum: i,
					stopStr: this.getTimeString(day.stop),
					startStr: this.getTimeString(day.start),
				};
		}
		for (let i = 0; i < dayNum; i++) {
			const day = this.provider?.hours[this.dayStr(i)];
			if (day.active)
				return {
					...day,
					dayStr: this.dayAbbrev(i),
					dayNum: i,
					stopStr: this.getTimeString(day.stop),
					startStr: this.getTimeString(day.start),
				};
		}
	}

	@action
	async init() {
		try {
			const base64Map = await getProviderBusinessLocationMarkerMap(
				{ providerId: this.provider._id },
				{ batch: true }
			);
			runInAction(() => {
				this.base64Map = base64Map;
			});
		} catch (err) {
			throw err;
		}
	}

	dayStr(dayNum) {
		const days = [
			"sunday",
			"monday",
			"tuesday",
			"wednesday",
			"thursday",
			"friday",
			"saturday",
		];
		return days[dayNum];
	}

	dayAbbrev(dayNum) {
		const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
		return days[dayNum];
	}

	getTimeString(time) {
		return getTimeString(time);
	}
}

export default ProfileLocationHoursStore;
